import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';

/**
 * Store dla zarządzania elementami tekstowymi
 */
const useTextStore = create(
  persist(
    (set, get) => ({
      // Stan elementów tekstowych
      textItems: {}, // { id: { id, x, y, text, fontSize, fontFamily, color, rotation, fontWeight, textAlign } }
      selectedTextId: null,
      isEditing: false, // Nowy stan: czy panel edycji jest otwarty
      
      // Stan przeciągania
      isDraggingText: false,
      dragStartPos: null,

      // Domyślne ustawienia dla nowego tekstu
      defaultSettings: {
        text: 'Nowy tekst',
        fontSize: 16,
        fontFamily: 'Inter',
        color: '#000000',
        fontWeight: 'normal',
        textAlign: 'center' // left, center, right
      },

      // Akcje
      setIsEditing: (status) => set({ isEditing: status }),

      addTextItem: (position, settings = {}, shouldSelect = true) => {
        const id = uuidv4();
        const defaults = get().defaultSettings;
        const newItem = {
          id,
          x: position.x,
          y: position.y,
          rotation: 0,
          text: settings.text || defaults.text,
          fontSize: settings.fontSize || defaults.fontSize,
          fontFamily: settings.fontFamily || defaults.fontFamily,
          color: settings.color || defaults.color,
          fontWeight: settings.fontWeight || defaults.fontWeight,
          textAlign: settings.textAlign || defaults.textAlign
        };
        set((state) => ({ 
          textItems: { ...state.textItems, [id]: newItem },
          selectedTextId: shouldSelect ? id : state.selectedTextId,
          isEditing: false // Dodając nowy tekst, nie wchodzimy od razu w tryb edycji (chyba że wymuszone inaczej)
        }));
        return id;
      },
      duplicateTextItem: (id) => {
        const state = get();
        const item = state.textItems[id];
        if (item) {
          const newId = uuidv4();
          const newItem = {
            ...item,
            id: newId,
            x: item.x + 20,
            y: item.y + 20
          };
          set((state) => ({
            textItems: { ...state.textItems, [newId]: newItem },
            selectedTextId: newId, // Zaznaczamy nowy element
            isEditing: false 
          }));
          return newId;
        }
      },
      removeTextItem: (id) => {
        set((state) => {
          const newItems = { ...state.textItems };
          delete newItems[id];
          return { 
            textItems: newItems,
            selectedTextId: state.selectedTextId === id ? null : state.selectedTextId,
            isEditing: state.selectedTextId === id ? false : state.isEditing
          };
        });
      },
      updateTextItem: (id, updates) => {
        set((state) => ({
          textItems: { ...state.textItems, [id]: { ...state.textItems[id], ...updates } }
        }));
      },
      updateDefaultSettings: (settings) => {
        set((state) => ({ defaultSettings: { ...state.defaultSettings, ...settings } }));
      },
      selectTextItem: (id) => {
        // Wybranie tekstu domyślnie NIE włącza edycji (tylko przesuwanie)
        set({ selectedTextId: id, isEditing: false });
      },
      startEditingText: (id) => {
        // Jawne włączenie edycji (np. double click)
        set({ selectedTextId: id, isEditing: true });
      },
      clearTextSelection: () => {
        set({ selectedTextId: null, isEditing: false });
      },

      // Dragging
      startDraggingText: (id, startPos) => {
        set({ isDraggingText: true, selectedTextId: id, dragStartPos: startPos });
      },
      updateDraggingText: (newPos) => {
        const { isDraggingText, selectedTextId } = get();
        if (!isDraggingText || !selectedTextId) return;

        get().updateTextItem(selectedTextId, { x: newPos.x, y: newPos.y });
      },
      stopDraggingText: () => {
        set({ isDraggingText: false, dragStartPos: null });
      }
    }),
    {
      name: 'floorcraft-text-storage',
      partialize: (state) => ({ 
        textItems: state.textItems,
        defaultSettings: state.defaultSettings 
      }),
    }
  )
);

export default useTextStore;